import * as React from "react";
import {VeboxUser} from "../../providers/VeboxUser";
import {VeboxModels} from "../../types/veboxModels";
import useRestData, {RestDataStatus} from "../../hooks/useRestData";
import {getHierarchies} from "../HierarchyVisibilityUtils";
import useCurrent = VeboxUser.useCurrentUser;

type CommonDataContextType = {
    locales: VeboxModels.Locale[],
    timezones: string[],
    hierarchiesTree: VeboxModels.HierarchiesTree[],
    hierarchiesTreeStatus: RestDataStatus,
    roles: VeboxModels.Role[],
    roleStatus: RestDataStatus
}

const listDataOptions = {
    mapResult: x => x.list
}

const supportRestDataOptions = {
    ...listDataOptions,
    withAuthentication: false,
}

const notImmediateOptions = {
    ...listDataOptions,
    immediate: false,
}

const CommonDataContext = React.createContext<CommonDataContextType>({} as any);

export const CommonDataProvider: React.FC = ({children}) => {
    const user = useCurrent();

    const {data: locales = []} = useRestData<VeboxModels.Locale[]>("support/locales", {}, supportRestDataOptions);
    const {data: timezones = []} = useRestData<string[]>("support/timezones", {}, supportRestDataOptions);
    const [hierarchiesTree, setHierarchiesTree] = React.useState<VeboxModels.HierarchiesTree[]>([])
    const [hierarchiesTreeStatus, setHierarchiesTreeStatus] = React.useState<RestDataStatus>(RestDataStatus.LOADING)
    const {data: roles = [], status: roleStatus = RestDataStatus.LOADING} = useRestData<VeboxModels.Role[]>(
        "users/current/roles/managed", {},
        listDataOptions)

    React.useEffect(() => {
        if (user && user.guid) {
            Promise.all([getHierarchies()])
                .then(([hierarchiesTreeStructure]) => {
                    setHierarchiesTree(hierarchiesTreeStructure);
                    setHierarchiesTreeStatus(RestDataStatus.LOADED)
                })
                .catch((error) => {
                    setHierarchiesTreeStatus(RestDataStatus.ERROR)
                    console.error(error)
                })
        }
    }, [user])

    return (
        <CommonDataContext.Provider
            value={{
                locales,
                timezones,
                hierarchiesTree,
                hierarchiesTreeStatus,
                roles,
                roleStatus
            }}
        >
            {children}
        </CommonDataContext.Provider>
    )
}

type CommonDataMap<T> = (x: CommonDataContextType) => T

export function useCommonData<T = CommonDataContextType>(map?: CommonDataMap<T>): T {
    const x = React.useContext(CommonDataContext)
    return map ? map(x) : x as any
}